import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Zap, Package, Navigation } from 'lucide-react';
import type { PortfolioSite } from '../../../redux/api/portfolioApi';
import { parseSiteLocation } from '../../../services/portfolioComponentService';

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];

function hashOffset(str: string): { latOff: number; lonOff: number } {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return {
    latOff: ((h & 0xffff) / 0xffff - 0.5) * 8,
    lonOff: (((h >> 16) & 0xffff) / 0xffff - 0.5) * 12,
  };
}

interface MappedSite {
  site: PortfolioSite;
  lat: number;
  lng: number;
  hasRealCoords: boolean;
}

function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    // Leaflet needs invalidateSize when rendered inside containers that
    // animate or change size (e.g. collapsible dashboard modules).
    const timer = setTimeout(() => map.invalidateSize(), 200);
    const observer = new ResizeObserver(() => map.invalidateSize());
    if (map.getContainer().parentElement) {
      observer.observe(map.getContainer().parentElement);
    }
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);
  return null;
}

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (positions.length > 0 && !fitted.current) {
      fitted.current = true;
      map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 10 });
    }
  }, [map, positions]);
  return null;
}

interface PortfolioMapModuleProps {
  sites: PortfolioSite[];
  onSiteClick?: (site: PortfolioSite) => void;
}

const PortfolioMapModule: React.FC<PortfolioMapModuleProps> = ({ sites, onSiteClick }) => {
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null);

  const mappedSites = useMemo<MappedSite[]>(() => {
    return sites.map((site) => {
      const parsed = parseSiteLocation(site);
      if (parsed) {
        return { site, lat: parsed.lat, lng: parsed.lng, hasRealCoords: true };
      }
      const id = site.site_id || site.id || site.site_name || '';
      const { latOff, lonOff } = hashOffset(id);
      return {
        site,
        lat: DEFAULT_CENTER[0] + latOff,
        lng: DEFAULT_CENTER[1] + lonOff,
        hasRealCoords: false,
      };
    });
  }, [sites]);

  const realPositions = useMemo<[number, number][]>(
    () => mappedSites.filter(s => s.hasRealCoords).map(s => [s.lat, s.lng]),
    [mappedSites]
  );

  if (sites.length === 0) {
    return (
      <div
        className="h-64 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
      >
        <p className="text-sm">No sites available</p>
      </div>
    );
  }

  return (
    <div className="h-72 rounded-lg overflow-hidden" style={{ background: '#0f172a' }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={4}
        className="h-full w-full"
        style={{ background: '#0f172a' }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <InvalidateOnMount />
        {realPositions.length > 0 && <FitBounds positions={realPositions} />}

        {mappedSites.map((ms) => {
          const siteId = ms.site.site_id || ms.site.id || '';
          const isHovered = hoveredSiteId === siteId;
          const capacity = ms.site.site_capacity_mw || ms.site.capacity_mw || 0;
          const size = Math.max(5, Math.min(4 + Math.log10(capacity + 1) * 4, 16));
          const name = ms.site.site_name || ms.site.name || 'Unknown';

          return (
            <CircleMarker
              key={siteId}
              center={[ms.lat, ms.lng]}
              radius={isHovered ? size * 1.4 : size}
              pathOptions={{
                fillColor: '#17c480',
                fillOpacity: ms.hasRealCoords ? (isHovered ? 0.9 : 0.7) : 0.25,
                color: isHovered ? '#fff' : '#17c480',
                weight: isHovered ? 2 : 0.5,
              }}
              eventHandlers={{
                click: () => onSiteClick?.(ms.site),
                mouseover: () => setHoveredSiteId(siteId),
                mouseout: () => setHoveredSiteId(null),
              }}
            >
              <Popup>
                <div className="min-w-40">
                  <h3 className="font-semibold text-gray-900 text-sm">{name}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                    {capacity > 0 && (
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {capacity} MW</span>
                    )}
                    {ms.site.entity_count != null && (
                      <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {ms.site.entity_count}</span>
                    )}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PortfolioMapModule;
